import type { AnySchema } from 'yup-v1-latest';
import { createExternalIDCatalogOrgConfig, EXTERNAL_ID_ENTITY_LABELS } from './externalIDTypesConfiguration';

export type OrgConfigValueType = 'boolean' | 'number' | 'string' | 'commaSeparatedList' | 'json';
export type OrgConfigFlagType = 'feature' | 'configuration' | 'migration';
export type OrgConfigUpdateWarning = (savedValue: unknown, proposedValue: unknown) => string | undefined;

export type OrgConfigKeySpec = {
  /** The dot-namespaced string sent to and received from the backend. */
  key: `${string}.${string}`;
  /** Human-readable explanation of what the key controls. */
  description: string;
  /** How the raw string value from the backend should be interpreted. */
  valueType: OrgConfigValueType;
  /** Optional validation schema for the parsed JSON value. */
  schema?: AnySchema;
  /** Optional formatted value that can seed the Add Configuration editor. */
  formattedDefaultValue?: string;
  /** Optional warning based on the parsed saved and proposed JSON values. */
  getUpdateWarning?: OrgConfigUpdateWarning;
  /** Classifies the nature of this configuration key (feature vs configuration vs migration).
   * Feature -> Activates a new part of the UI
   * Configuration -> Adjusts user parameters
   * Migration -> Key may be removed in a future release
   */
  flagType: OrgConfigFlagType;
  /**
   * Only relevant when flagType is 'migration'.
   * A version string indicating when this key should be reviewed for removal.
   */
  targetEnd?: `v${string}.${string}` | `UI v${string}.${string}`;
};

export const OrgConfigurationKeys = {
  AdditionalCustomerCreditCurrencies: {
    key: 'ui.AdditionalCustomerCreditCurrencies',
    description:
      'Add additional currencies to dealer customer credit screen limit dropdown. Expected as comma separated list of Currency.Name',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  AllowSetCustomerBalance: {
    key: 'ui.AllowSetCustomerBalance',
    description: 'If true, allows authorized users to set customer balance in dealer customer balances screen',
    valueType: 'boolean',
    flagType: 'feature',
  },

  AllowCustomerOptionManualExpiry: {
    key: 'ui.AllowCustomerOptionManualExpiry',
    description:
      'If true, allows manually expiring customer options positions from dealer customer margin and customer positions. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  BlotterRowsMax: {
    key: 'ui.BlotterRowsMax',
    description: 'Sets the default number of rows scrolled in most websocket blotters. Default is 10,000.',
    valueType: 'number',
    flagType: 'configuration',
  },

  CacheVersion: {
    key: 'ui.CacheVersion',
    description:
      "Sets the UI reference data cache version. Do this to force a refresh of the cache. Tell UI team if this is ever used. Default is '1'.",
    valueType: 'string',
    flagType: 'configuration',
  },

  ClickToTradeTimeInForce: {
    key: 'ui.ClickToTradeTimeInForce',
    description:
      "Sets the TimeInForce parameter for the ClickToTrade feature. No validation is done on this value. Default is 'GoodTillCancel'.",
    valueType: 'string',
    flagType: 'configuration',
  },

  CommonTradeSpotPositionCurrencies: {
    key: 'ui.CommonTradeSpotPositionCurrencies',
    description:
      'Sets the currencies which should be regarded as "common" and thus be surfaced in the trade spot position selector dropdown. Expected as comma separated list of Currency.Name',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  CustomerBalanceShowForceWithdraw: {
    key: 'ui.CustomerBalanceShowForceWithdraw',
    description:
      'If true, allows users to "force withdraw" on dealer/balances New Balance Transaction. Defaults to true for Talos users.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  RequireAdditionalConfirmOnBalanceTransactionPreview: {
    key: 'ui.RequireAdditionalConfirmOnBalanceTransactionPreview',
    description:
      'If true, requires an additional confirmation modal before submitting balance transactions. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  CustomerBalanceRequireForceWithdrawConfirm: {
    key: 'ui.CustomerBalanceRequireForceWithdrawConfirm',
    description:
      'If true, requires typing "confirm" in the balance transaction drawer before previewing or submitting a force withdrawal. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  CustomerConfigurationRowsMax: {
    key: 'ui.CustomerConfigurationRowsMax',
    description: 'Sets the default number of rows to show in the customer configuration blotter. Default is 10_000.',
    valueType: 'number',
    flagType: 'configuration',
  },

  CustomerConfigurationsPageSize: {
    key: 'ui.CustomerConfigurationsPageSize',
    description: 'Sets the REST paging size for the customer configurations screen. Default is 500.',
    valueType: 'number',
    flagType: 'configuration',
  },

  CustomerSymbolGroupsRowsMax: {
    key: 'ui.CustomerSymbolGroupsRowsMax',
    description: 'Sets the default number of rows to show in the customer symbol groups blotter. Default is 10_000.',
    valueType: 'number',
    flagType: 'configuration',
  },

  CustomerSymbolGroupsPageSize: {
    key: 'ui.CustomerSymbolGroupsPageSize',
    description: 'Sets the REST paging size for the customer symbol groups screen. Default is 500.',
    valueType: 'number',
    flagType: 'configuration',
  },

  CustomerPricingCopyDetailsString: {
    key: 'ui.CustomerPricingCopyDetailsString',
    description:
      "Sets additional text to be added to the end of the customer pricing calculator's details when the copy button is pressed.",
    valueType: 'string',
    flagType: 'configuration',
  },

  CustomerSettlementTradeRowsMax: {
    key: 'ui.CustomerSettlementTradeRowsMax',
    description: 'Sets the default number of rows to show in the trade settlement blotter. Default is 5,000.',
    valueType: 'number',
    flagType: 'configuration',
  },

  CustomerSummaryDelayMs: {
    key: 'ui.CustomerSummaryDelayMs',
    description:
      'Sets the delay the UI will wait before asking the backend for Customer Deal Summaries. Use this to prevent too many failed lookups if BE is behind. Default is 0.',
    valueType: 'number',
    flagType: 'configuration',
  },

  DuplicateSubAccountRelatedDataFailureTimeoutMs: {
    key: 'ui.DuplicateSubAccountRelatedDataFailureTimeoutMs',
    description:
      'Sets a delay for the UI to wait before freaking out about missing data when cloning a sub account. (ms). Default is 5,000.',
    valueType: 'number',
    flagType: 'configuration',
  },

  AllowOldMarketSelector: {
    key: 'ui.AllowOldMarketSelector',
    description: 'Allows users to opt out of the new Market Selector with their trading setting. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableJSProfiling: {
    key: 'ui.EnableJSProfiling',
    description: 'If true, enables browser JS profiling for this organization on the next page load.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  UseNewMarketSelectorInTradingAggregations: {
    key: 'ui.UseNewMarketSelectorInTradingAggregations',
    description: 'Enables the new Market Selector specifically on Admin > Trading Aggregations. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableNewMarketSelectorMSSValidation: {
    key: 'ui.EnableNewMarketSelectorMSSValidation',
    description:
      'For the new market selector, will opt in to also apply the async MarketSecurityStatus validation step. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableInlineMultilegMarketEditing: {
    key: 'ui.EnableInlineMultilegMarketEditing',
    description:
      'Enables editing multileg leg markets directly from inline surfaces such as the Market Data Card quick market selector and Price Grid. These edits update the instrument definition for every user on the org. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  DisableUnifiedLiquidityDerivativesMarketSelectionRestriction: {
    key: 'ui.DisableUnifiedLiquidityDerivativesMarketSelectionRestriction',
    description:
      'Disables the restriction on market account selector that prevents more than one market account being selected per market. Applies only when Unified Liquidity is enabled on a Perp or Swap.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableUnifiedLiquidityCrossInstrumentRequest: {
    key: 'ui.EnableUnifiedLiquidityCrossInstrumentRequest',
    description:
      'When true, the Unified Liquidity token subscription requests cross-instrument tokens. Defaults to false during rollout.',
    valueType: 'boolean',
    flagType: 'migration',
    targetEnd: 'v2.59',
  },

  EnableUnifiedLiquidityMarketSymbols: {
    key: 'ui.EnableUnifiedLiquidityMarketSymbols',
    description:
      'Enables per-market-account Unified Liquidity symbol selection and MarketSymbols order and market-data requests. Defaults to false during rollout. Requires 2.58 backend',
    valueType: 'boolean',
    flagType: 'migration',
    targetEnd: 'v2.59',
  },

  EnableArchiveOrders: {
    key: 'ui.EnableArchiveOrders',
    description: 'Enables the ability to archive orders',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableBulkClosePosition: {
    key: 'ui.EnableBulkClosePosition',
    description: 'Enables the ability to bulk close positions from the positions blotter',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableBulkModifyOrders: {
    key: 'ui.EnableBulkModifyOrders',
    description: 'Enables bulk order end-time modification from the orders blotter. Defaults to true.',
    valueType: 'boolean',
    flagType: 'migration',
    targetEnd: 'v15.0',
  },

  EnableLegacyUpdateBalances: {
    key: 'ui.EnableLegacyUpdateBalances',
    description:
      'When true, Positions blotter uses legacy "Update Balances" (PUT /positions/market-accounts via UpdatePositionsDialog) instead of "Adjust Market Account Position" (POST /asset-transactions).',
    valueType: 'boolean',
    flagType: 'migration',
    targetEnd: 'v2.54',
  },

  EnableBuyingPowerMonitoringBlotter: {
    key: 'ui.EnableBuyingPowerMonitoringBlotter',
    description: 'Enables the monitoring buying power blotter',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCache: {
    key: 'ui.EnableCache',
    description:
      'Enables UI reference data caching. Default is true. If you need to set this down please contact the UI team.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCalendarSpreads: {
    key: 'ui.EnableCalendarSpreads',
    description: 'Enables trading native calendar spreads in the UI. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableSignals: {
    key: 'ui.EnableSignals',
    description: 'Enables the Signals feature in the UI. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCFDs: {
    key: 'ui.EnableCFD',
    description: 'Enables trading CFDs in the UI. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableGovernmentBonds: {
    key: 'ui.EnableGovernmentBonds',
    description: 'Enables trading government bonds in the UI. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableShares: {
    key: 'ui.EnableShares',
    description: 'If true, enables Share product type in Manual Trade booking and modify. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableSEDOLOutput: {
    key: 'secmaster.EnableSEDOLOutput',
    description:
      'When true, secmaster includes SEDOL in security metadata. UI shows SEDOL identifier columns and includes SEDOL in search and metadata display. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableGovernmentBond32nds: {
    key: 'ui.EnableGovernmentBond32nds',
    description:
      'When true, government bond Mark Price and Manual Trade use Treasury 32nds notation (e.g. 99-032, 99-03+). Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  UnifiedLiquidityCombinePerpsAndCFDs: {
    key: 'oms.UnifiedLiquidityCombinePerpsAndCFDs',
    description: 'When true, Unified Liquidity combines Perps and CFDs and labels the group as Perp/Swap.',
    valueType: 'boolean',
    flagType: 'configuration',
  },

  EnableRFQFormOptions: {
    key: 'ui.EnableRFQFormOptions',
    description: 'Set to true to show the Options selector in the RFQ Form',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableRFQFormOTCs: {
    key: 'ui.EnableRFQFormOTCs',
    description: 'Set to true to show the OTC product group selector in the RFQ Form.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableSTSDigitalCustomOptionRFQ: {
    key: 'ui.EnableSTSDigitalCustomOptionRFQ',
    description: 'Set to true to allow custom expiry and strike entry for STS Digital option RFQs.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableRFQSweeping: {
    key: 'ui.EnableRFQSweeping',
    description: 'Set to true to enable RFQ sweeping in the RFQ Form and Quotes V2 blotter.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  SweepGroupIDQuotesOnlySweepable: {
    key: 'ui.SweepGroupIDQuotesOnlySweepable',
    description:
      'When true, Quotes V2 disables direct buy, sell, and open actions for quotes with a SweepGroupID so they can only be traded from the sweeping panel.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  RFQSweeperAutoCancelRemainingAfterExecution: {
    key: 'ui.RFQSweeperAutoCancelRemainingAfterExecution',
    description:
      'Set to true to auto-cancel remaining open, non-traded RFQ sweeper quotes after all selected fills execute successfully.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableRFQSizeCounteringMultitokenCompound: {
    key: 'ui.EnableRFQSizeCounteringMultitokenCompound',
    description: 'Set to true to show the Size Countering toggle for multi-token Compound instruments in the RFQ Form.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableRFQSizeCounteringSpot: {
    key: 'ui.EnableRFQSizeCounteringSpot',
    description: 'Set to true to show the Size Countering toggle for Spot instruments in the RFQ Form.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  RFQFormClearingAccountProductTypes: {
    key: 'ui.RFQFormClearingAccountProductTypes',
    description:
      'When set, these product types will show the optional Clearing Account field for negotiated RFQs in the RFQ Form.',
    valueType: 'json',
    flagType: 'feature',
  },

  SizeCounteringImpliesPartialFill: {
    key: 'ui.SizeCounteringImpliesPartialFill',
    description: 'When true, RFQs sent with SizeCountering enabled also send PartialFill enabled. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableRfqSoundEffects: {
    key: 'ui.EnableRfqSoundEffects',
    description: 'If true, enables sound effects for manual RFQ interactions. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCreditLimitsPerMarketAccount: {
    key: 'ui.EnableCreditLimitsPerMarketAccount',
    description:
      'Enables the ability to set credit limits per customer market account in dealer customer credit screen. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCurrenciesFilterSelectAll: {
    key: 'ui.EnableCurrenciesFilterSelectAll',
    description: 'Adds a select all button for currencies in the balances blotter. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCustomerAccountRestrictions: {
    key: 'ui.EnableCustomerAccountRestrictions',
    description: 'If true, allows users to set customer account restrictions in dealer customer users screen',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCustomerTradingLimitsPerAccount: {
    key: 'ui.EnableCustomerTradingLimitsPerAccount',
    description:
      'If true, allows users to set customer trading limits per account in dealer customer users screen. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableDerivativesPositionsBlotterAdvanced: {
    key: 'ui.EnableDerivativesPositionsBlotterAdvanced',
    description: 'If true shows the advanced derivatives positions blotter in principal',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableMonitoringBlotters: {
    key: 'ui.EnableMonitoringBlotters',
    description: 'If true, allows users to see and use the monitoring blotters. Default is true for Talos users.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableDailyAnalytics: {
    key: 'ui.EnableDailyAnalytics',
    description: 'Enable daily Analytics reports in the UI. Defaults to false. Talos users always have access.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  DailyAnalyticsTrialEndDate: {
    key: 'ui.DailyAnalyticsTrialEndDate',
    description: 'The trial end date for daily analytics. Defaults to empty string.',
    valueType: 'string',
    flagType: 'configuration',
  },

  EnablePortfolioSettlement: {
    key: 'ui.EnablePortfolioSettlement',
    description: 'If true, shows the portfolio settlement screen',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableOrderAuxData: {
    key: 'ui.EnableOrderAuxData',
    description:
      'If true, requests auxiliary data for orders. Required for DDH and archive orders features. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableOTCDeltaStrikeSupport: {
    key: 'ui.EnableOTCDeltaStrikeSupport',
    description:
      'If true, OTC vanilla option builder sends delta strikes in StrikeDelta and enables Delta strike type in UI. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableRepoProduct: {
    key: 'ui.EnableRepoProduct',
    description: 'If true, shows the Repo product type in the Deal Product Builder. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableLoanOpenTermSupport: {
    key: 'ui.EnableLoanOpenTermSupport',
    description:
      'When true, enables Open Term loans in Product Builder and shows Open Term loan product templates in Manual Deal. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableDealCashflows: {
    key: 'ui.EnableDealCashflows',
    description: 'When true, shows deal cashflows entry points. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableOTCPhysicalSettlement: {
    key: 'ui.EnableOTCPhysicalSettlement',
    description:
      'When true, enables Physical settlement for OTC forwards/options in Product Builder and Physical-settlement product templates in Manual Deal. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableMarginManagementFeatures: {
    key: 'ui.EnableMarginManagementFeatures',
    description: 'Enables the new margin management related features that were released in 2.51.0',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnablePortfolioOverviewHud: {
    key: 'ui.EnablePortfolioOverviewHud',
    description: 'If true, shows KPI HUD on Portfolio Management Overview. Defaults to false.',
    valueType: 'boolean',
    flagType: 'migration',
  },

  EnableNetAllPosition: {
    key: 'ui.EnableNetAllPosition',
    description: 'If true, allows users to create asymmetric exposure limits. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableNonNativeOHLCResolutions: {
    key: 'ui.EnableNonNativeOHLCResolutions',
    description:
      'If true, enables additional non-native OHLC resolutions (10M, 15M, 30M, 1W, 1MO) in the candlestick chart. Requires backend flag NonNativeOHLCResolutionsEnabled to be enabled. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  CreditBlotterCalculationTypes: {
    key: 'ui.CreditBlotterCalculationTypes',
    description:
      'Sets the calculation types for the credit blotter. Expected as comma separated list of CalculationType.Name. Defaults to BuyingPower.',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  EnablePriceGridV2: {
    key: 'ui.EnablePriceGridV2',
    description: 'If true, shows the new generic Price Grid V2 panel. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableMultilegPriceGrid: {
    key: 'ui.enableMultilegPriceGrid',
    description: 'Enable the Multileg Price Grid in the UI. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCustomerPriceGridV2: {
    key: 'ui.EnableCustomerPriceGridV2',
    description: 'If true, shows the new Customer Price Grid V2 panel. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableReconMismatchesBlotter: {
    key: 'ui.EnableReconMismatchesBlotter',
    description: 'If true, shows the recon mismatches blotter',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableDerivativesPositionsBlotter: {
    key: 'ui.EnableDerivativesPositionsBlotter',
    description:
      'Older per-org toggle for the Derivatives Positions tab. Defaults to true. The tab is shown only when this is true AND ui.EnableLegacyPositionsTabs is true. ui.EnableLegacyPositionsTabs is the retirement switch.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableUnifiedPositionsBlotter: {
    key: 'ui.EnableUnifiedPositionsBlotter',
    description: 'Enable the Market Accounts tab in the Positions Blotter. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableLegacyPositionsTabs: {
    key: 'ui.EnableLegacyPositionsTabs',
    description:
      'Retirement switch for the legacy Derivatives Positions tab, including custom Derivatives tabs. Defaults to true. Set to false to hide. Also requires ui.EnableDerivativesPositionsBlotter.',
    valueType: 'boolean',
    flagType: 'migration',
  },

  EnableCreditBlotter: {
    key: 'ui.EnableCreditBlotter',
    description: 'If true, shows the Credit blotter in the UI. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableTransfersBlotter: {
    key: 'ui.EnableTransfersBlotter',
    description: 'If true, shows the Transfers blotter in the UI. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  MaxRecentTransferAccounts: {
    key: 'ui.MaxRecentTransferAccounts',
    description:
      'The maximum number of accounts shown in Transfer Form source/destination most recently used lists. Defaults to 3. Valid range 1–20.',
    valueType: 'number',
    flagType: 'configuration',
  },

  EnablePerformancePageDerivatives: {
    key: 'ui.EnablePerformancePageDerivatives',
    description:
      'If true, will allow the user to select "Derivatives" in the Portfolio > Performance page product type selector.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableTradingControls: {
    key: 'ui.EnableTradingControls',
    description:
      'If true, shows additional blotter for controlling Market Accounts Trading capabilities. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableTradingControlsDefaultSettings: {
    key: 'ui.EnableTradingControlsDefaultSettings',
    description: 'If true, Default Settings button/modal in Trading Controls is shown. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  BypassAccountSegregationChecksForTradingControlsSettings: {
    key: 'ui.BypassAccountSegregationChecksForTradingControlsSettings',
    description:
      'If enabled, Trading Configuration and Trading Controls Admin users can view and manage all market accounts in Trading Controls, bypassing Account Segregation filters. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableWalletMonitoring: {
    key: 'ui.EnableWalletMonitoring',
    description: 'If true, will show the wallet monitoring related functionality in the ui',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableAtlasWalletMonitoring: {
    key: 'ui.EnableAtlasWalletMonitoring',
    description:
      'If true, non-EVM wallet addresses on the monitoring wallet form are treated as Atlas accounts with Atlas market currencies',
    valueType: 'boolean',
    flagType: 'feature',
  },

  HomeCurrencyAdditionalCurrencies: {
    key: 'ui.HomeCurrencyAdditionalCurrencies',
    description:
      'Additional currencies to be included when selecting a home currency for the UI. Expected as comma separated list of Currency.Name',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  OrderRowsMax: {
    key: 'ui.OrderRowsMax',
    description: 'Sets the max number of rows the order blotter will load before showing warning. Default is 10,000.',
    valueType: 'number',
    flagType: 'configuration',
  },

  OrgSecmasterEditDelay: {
    key: 'ui.OrgSecmasterEditDelay',
    description:
      'Sets the amount of time the UI will wait between requests for a "bulk" operation in the org secmaster screen. Default is 100.',
    valueType: 'number',
    flagType: 'configuration',
  },

  PlaceOrderThrottleMillis: {
    key: 'ui.PlaceOrderThrottleMillis',
    description:
      'Sets the amount of time the UI will wait between requests in the csv order upload screen. Default is 400.',
    valueType: 'number',
    flagType: 'configuration',
  },

  PriceGridMaxActiveSubscriptions: {
    key: 'ui.PriceGridMaxActiveSubscriptions',
    description: 'Sets the max number of subscriptions the price grid will allow. Default is 80.',
    valueType: 'number',
    flagType: 'configuration',
  },

  PricingRuleAllowedCrossMarkets: {
    key: 'ui.PricingRuleAllowedCrossMarkets',
    description: 'If set, allows user to set pricing rules that allow fills with a synthetic cross.',
    valueType: 'commaSeparatedList',
    flagType: 'feature',
  },

  PricingRuleAllowedCrossCurrencies: {
    key: 'ui.PricingRuleAllowedCrossCurrencies',
    description: 'If set, allows user to set currencies available for synthetic crosses',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  CustomerSecurityAllowedUnderlyingProductTypes: {
    key: 'ui.CustomerSecurityAllowedUnderlyingProductTypes',
    description: 'Sets available product types for Underlying Symbol in Customer Security Master',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  PricingRulesPageSize: {
    key: 'ui.PricingRulesPageSize',
    description: 'Sets the number of pricing rules loaded per page in the pricing rules screen. Default is 5,000.',
    valueType: 'number',
    flagType: 'configuration',
  },

  ShowAnalyticsMarketUptimesChart: {
    key: 'ui.analytics.ShowMarketUptimesChart',
    description: 'If true, shows the uptime chart in the advanced analytics screens',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableExecutionMarkoutsByMarketChart: {
    key: 'ui.analytics.EnableExecutionMarkoutsByMarketChart',
    description:
      'If true, shows the Execution Markouts by Market chart in the strategy details analytics drilldown. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableMarketPriceChart: {
    key: 'ui.analytics.EnableMarketPriceChart',
    description: 'If true, shows the Market Price chart in the symbol details analytics drilldown. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableAnalyticsInTrading: {
    key: 'ui.analytics.EnableAnalyticsInTrading',
    description: 'Moves Analytics into the Trading navigation and route hierarchy. Defaults to false during rollout.',
    valueType: 'boolean',
    flagType: 'migration',
  },

  ShowBlotterPauseButton: {
    key: 'ui.ShowBlotterPauseButton',
    description: 'If true, shows the pause subscription button in blotters',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableMultilegTradeFilter: {
    key: 'ui.EnableMultilegTradeFilter',
    description:
      'If true, shows the multileg trade filter dropdown in the Trade Blotter and Order Details → Trades tab. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowCustomerDepositAddressSelection: {
    key: 'ui.ShowCustomerDepositAddressSelection',
    description: 'If true, allows customer addresses to be selectable in the portfolio addresses screen',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowCustomerDepositSourceAccount: {
    key: 'ui.ShowCustomerDepositSourceAccount',
    description: 'If true, shows the source account column in the customer deposit address screen',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowCustomerWithdrawalAddresses: {
    key: 'ui.ShowCustomerWithdrawalAddresses',
    description: 'If true, shows the withdrawal addresses tab in the Customers screen. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowCustomerOrderAcceptanceRules: {
    key: 'ui.ShowCustomerOrderAcceptanceRules',
    description: 'If true, shows the customer order acceptance rules screen in dealer',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowCustomerRFQProtocolRules: {
    key: 'ui.ShowCustomerRFQProtocolRules',
    description: 'If true, shows the customer RFQ protocol rules screen in dealer. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  AllowSyntheticRFQ: {
    key: 'customer.AllowSyntheticRFQ',
    description:
      'If true, the Synthetic option is available as an RFQ Protocol in the RFQ Protocol Rules screen. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowCustomerTiering: {
    key: 'ui.ShowCustomerTiering',
    description:
      'If true, shows the customer configuration / pricing tiers / symbol groups blotters in dealer. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowCustomerFIXConnections: {
    key: 'ui.ShowCustomerFIXConnections',
    description: 'If true, shows the customer FIX connections screen in dealer. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  AllowClientFIXConnectionCreation: {
    key: 'ui.AllowClientFIXConnectionCreation',
    description: 'If true, allows dealers to create their own FIX connections. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowCustomerAccountSelectorForCustomerFIX: {
    key: 'ui.ShowCustomerAccountSelectorForCustomerFIX',
    description: 'If true, shows the customer account selector on the customer FIX connection form. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowCustomerNetAcquisition: {
    key: 'ui.ShowCustomerNetAcquisition',
    description: 'If true, shows the customer net acquisition blotters in dealer. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowCustomerPricingRulesBeta: {
    key: 'ui.ShowCustomerPricingRulesBeta',
    description: 'If true, shows the customer pricing rules (beta) screen in dealer. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowCustomerPricingRulesLegacy: {
    key: 'ui.ShowCustomerPricingRulesLegacy',
    description: 'If true, shows the customer pricing rules (legacy) screen in dealer. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowExchangeAddresses: {
    key: 'ui.ShowExchangeAddresses',
    description: 'If true, shows Exchanges tab in Addresses',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowMultiLegSecmaster: {
    key: 'ui.ShowMultiLegSecmaster',
    description:
      'If true, shows the multileg secmaster screen. Will use the Market status of multileg by default but can be forced by setting to true',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowPositionsBlotterWarningColumn: {
    key: 'ui.ShowPositionsBlotterWarningColumn',
    description: 'If true, shows the warning column on the positions blotter',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowSecurityMasterUnifiedLiquidityColumn: {
    key: 'ui.ShowSecurityMasterUnifiedLiquidityColumn',
    description: 'If true, shows the security master unified liquidity column. Default to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowSecurityMasterFillIncrementColumns: {
    key: 'ui.ShowSecurityMasterFillIncrementColumns',
    description: 'If true, shows the security master fill increment columns. Default to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowSecurityMasterUseUnderlyingSecurityForRFQ: {
    key: 'ui.ShowSecurityMasterUseUnderlyingSecurityForRFQ',
    description: 'If true, shows the UseUnderlyingSecurityForRFQ column and drawer field. Default to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCreateCustomSecurity: {
    key: 'ui.EnableCreateCustomSecurity',
    description:
      'If true, enables the "Create Custom Security" workflow button on the Customer Security Master page. Defaults to false (true for Talos users).',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCounterpartyOverrideCustomerSecurityMaster: {
    key: 'ui.EnableCounterpartyOverrideCustomerSecurityMaster',
    description: 'If true, enables counterparty overrides in Customer Security Master. Defaults to true.',
    valueType: 'boolean',
    flagType: 'migration',
    targetEnd: 'v2.58',
  },

  CounterpartyExternalIDTypes: createExternalIDCatalogOrgConfig(
    'ui.CounterpartyExternalIDTypes',
    EXTERNAL_ID_ENTITY_LABELS.Counterparty
  ),

  EnableHeaderSupportMenu: {
    key: 'ui.ShowSupportMenu',
    description: 'If true, shows the support menu button in the UI header. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowSupportChat: {
    key: 'ui.ShowSupportChat',
    description: 'If true, shows the support chat (intercom) button in the UI. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableAvaAI: {
    key: 'ui.EnableAvaAI',
    description: 'If true, enables the Talos AI chat in the UI. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  SubAccountPositionsBlotterMode: {
    key: 'ui.SubAccountPositionsBlotterMode',
    description:
      'Sets mode of sub accounts positions blotter. Valid modes are: "Spot", "Derivatives", "DerivativesAdvanced"',
    valueType: 'string',
    flagType: 'configuration',
  },

  SupportedLayoutArrangements: {
    key: 'ui.SupportedLayoutArrangements',
    description:
      'Sets the allowed layout arrangements which can be selected in the dealer users screen valid arrangements are "SingleSymbolView", "MarketDataCards". Value should be comma separated list',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  SupportedStrategiesForPricingModes: {
    key: 'ui.SupportedStrategiesForPricingModes',
    description:
      'Sets the allowed strategies when using a specific limit price mode. For example if "Iceberg" is included in this list, then if Security.SupportedPricingModes.ImpliedVolatility is true, users will be able to place iceberg orders with limit prices specified in implied volatility. Value should be a comma separated list of Strategy.Name',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  TradeRowsMax: {
    key: 'ui.TradeRowsMax',
    description: 'Sets the default number of rows to show in the trade blotter. Default is 10,000.',
    valueType: 'number',
    flagType: 'configuration',
  },

  RecentOrderRowsMax: {
    key: 'ui.RecentOrderRowsMax',
    description: "The max amount of orders to load in for the UI's RECENT_ORDERS subscription. Default is 10,000.",
    valueType: 'number',
    flagType: 'feature',
  },

  ViewWhitelabelMenuItems: {
    key: 'ui.ViewWhitelabelMenuItems',
    description:
      'If true it allows users to see the white label specific navigation items in the dealer menu. Defaults to true for Talos users.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableAllocationControlsPage: {
    key: 'ui.EnableAllocationControlsPage',
    description: 'Enable allocation controls page in the UI settings. Defaults to true for talos users.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableOrderFormSubAccountCreation: {
    key: 'ui.EnableOrderFormSubAccountCreation',
    description:
      'If true, allows the user to create and clone sub accounts from the sub account selector in the order form',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableSalesOrderAdvancedFeeOptions: {
    key: 'ui.EnableSalesOrderAdvancedFeeOptions',
    description: 'If true, shows the Advanced Fee Options section in the Sales Order form. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableSalesRFQSubscriptionStartDate: {
    key: 'ui.EnableSalesRFQSubscriptionStartDate',
    description:
      'If true, sends a StartDate (now − 2 min) on the CustomerQuote and CustomerExecutionReport subscriptions opened by the Sales RFQ form, preventing full-history replay. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableHierarchicalSubAccountPositionsBlotter: {
    key: 'ui.EnableHierarchicalSubAccountPositionsBlotter',
    description:
      'If true, allows the user to view the sub account positions blotter in hierarchical / tree mode. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableSubAccountHistoricalRecalc: {
    key: 'ui.EnableSubAccountHistoricalRecalc',
    description:
      'If true, exposes Sub Account historical recalculation from the Positions Sub Accounts blotter. Defaults to false for clients; Talos users can access even when the org flag is off.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableLpSettlement: {
    key: 'ui.EnableLpSettlement',
    description: 'If true, enables the LP Settlement screen in portfolio/LP-Settlement',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableLpSettlementLegacy: {
    key: 'ui.EnableLpSettlementLegacy',
    description:
      'If true, shows LP Settlement (Legacy) when the existing LP Settlement / companion-flag formula allows it. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  LPSettlementEligibleExchangeCounterparties: {
    key: 'ui.LPSettlementEligibleExchangeCounterparties',
    description: 'Comma-delimited Exchange counterparty names eligible for LP Settlement configuration.',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  EnableLpTransferAmount: {
    key: 'ui.EnableLpTransferAmount',
    description: 'If true, enables editable transfer amounts on LP Settlement initiate transfers. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableOtcSettlementWithLpSettlement: {
    key: 'ui.EnableOtcSettlementWithLpSettlement',
    description: 'If true, shows OTC Settlement even when LP Settlement is enabled',
    valueType: 'boolean',
    flagType: 'feature',
  },

  FreezeLpSettlementTransferTargets: {
    key: 'ui.FreezeLpSettlementTransferTargets',
    description:
      'If true, freezes Provider/Source/Destination selection on the LP Settlement batch detail drawer (amount is editable if ui.EnableLpTransferAmount is true). Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnablePortfolioManagementSystem: {
    key: 'ui.EnablePortfolioManagementSystem',
    description: 'If true, enables the Portfolio Management System layout mode',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnablePMSPENavigationMigration: {
    key: 'ui.EnablePMSPENavigationMigration',
    description:
      'If true, enables the migrated PMS/Portfolio Engineering navigation that splits Portfolio, Operations, Research, and Advanced Portfolio routes. Defaults to true when unset.',
    valueType: 'boolean',
    flagType: 'migration',
  },

  EnableDecimalAlignment: {
    key: 'ui.EnableDecimalAlignment',
    description:
      'If true, Position Qty on Portfolio Overview, Performance, and Risk blotters shares the coarsest decimal precision across instrument leaves under the same Underlying group. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  PMSOverviewTopUnderlyingCount: {
    key: 'ui.PMSOverviewTopUnderlyingCount',
    description:
      'Number of top and bottom underlying positions shown when the Portfolio Overview Top N filter is active. Defaults to 5.',
    valueType: 'number',
    flagType: 'configuration',
  },

  EnablePortfolioManagementReportsForPMSLite: {
    key: 'ui.EnablePortfolioManagementReportsForPMSLite',
    description:
      'If true, allows PMS Lite users to view the Portfolio Management Reports page (for legacy Portfolio Performance view)',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnablePMSIborAborReport: {
    key: 'ui.EnablePMSIborAborReport',
    description: 'If true, enables the IBOR/ABOR report in the Portfolio Management Reports page',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnablePMSIborAborRecon: {
    key: 'ui.EnablePMSIborAborRecon',
    description:
      'If true, enables the IBOR-ABOR Reconciliation tab on the Portfolio Operations Reconciliation page. Independent of ui.EnablePMSIborAborReport.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnablePortfolioComplianceAdminRole: {
    key: 'ui.EnablePortfolioComplianceAdminRole',
    description:
      'If true, user admins can see and assign the Portfolio Compliance Admin role. Talos users are not gated. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  DisableCustomerFilteringByConfigurations: {
    key: 'ui.DisableCustomerFilteringByConfigurations',
    description:
      'If set to true, the Sales Order and Sales RFQ customers filtering will ignore Customer Configurations. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableBalancesBlotter: {
    key: 'ui.EnableBalancesBlotter',
    description: 'If true, enables the Balances blotter in the Trading screen. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableRestrictedTrading: {
    key: 'ui.EnableRestrictedTrading',
    description:
      'When enabled, restricts Credit and Balances visibility for users without the VIEW_CREDIT_AND_BALANCES permission. When used with Trading Account Segregation, non-admin users also have restricted access to markets. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  RestrictedTradingHiddenMarkets: {
    key: 'ui.RestrictedTradingHiddenMarkets',
    description:
      'JSON array of Market.Name values to hide as market-level options in Market Data selectors when restricted trading is enabled. Permissioned market accounts within these markets remain available.',
    valueType: 'json',
    flagType: 'configuration',
  },

  EnableDelta1BPSPricingMode: {
    key: 'ui.EnableDelta1BPSPricingMode',
    description: 'Enables the Delta-1 BPS Pricing feature in the UI. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableDurationSubmission: {
    key: 'ui.EnableDurationSubmission',
    description:
      'If true, new orders send Duration when Duration mode is used for EndTime. Modify requests still send EndTime for started orders; only not-started modifies (Staged, and WaitingForStartTime when enabled) send Duration. If false (default), only staged orders send Duration.',
    valueType: 'boolean',
    flagType: 'migration',
    targetEnd: 'UI v3.0.0',
  },

  EnableOnlyCareOrderRelatedOrderSubmission: {
    key: 'ui.EnableOnlyCareOrderRelatedOrderSubmission',
    description:
      'When true, only RFQs coupled to a Care Order may be submitted; direct order submission and RFQs without a connection to a CO is disabled.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  AdminCredentialsBlacklist: {
    key: 'Admin.CredentialsBlacklist',
    description: '',
    valueType: 'commaSeparatedList',
    flagType: 'feature',
  },

  AdminCredentialsWhitelist: {
    key: 'Admin.CredentialsWhitelist',
    description: '',
    valueType: 'commaSeparatedList',
    flagType: 'feature',
  },

  AdminUserAdminCanAddRoles: {
    key: 'admin.UserAdmin.CanAddRoles',
    description: '',
    valueType: 'commaSeparatedList',
    flagType: 'feature',
  },

  AdminUserAdminCanEditOwnRoles: {
    key: 'admin.UserAdmin.CanEditOwnRoles',
    description: '',
    valueType: 'boolean',
    flagType: 'feature',
  },

  AdminUserAdminViewUserAPIKey: {
    key: 'admin.UserAdmin.ViewUserAPIKey',
    description: '',
    valueType: 'boolean',
    flagType: 'feature',
  },

  AdminUserAdminEditUserAPIKey: {
    key: 'admin.UserAdmin.EditUserAPIKey',
    description: '',
    valueType: 'boolean',
    flagType: 'feature',
  },

  AdminUserAdminAdminUserAPIKey: {
    key: 'admin.UserAdmin.AdminUserAPIKey',
    description: '',
    valueType: 'boolean',
    flagType: 'feature',
  },

  AdminUserAPIKeyCanAddRoles: {
    key: 'admin.UserAPIKey.CanAddRoles',
    description: '',
    valueType: 'commaSeparatedList',
    flagType: 'feature',
  },

  EnableServiceAccounts: {
    key: 'ui.EnableServiceAccounts',
    description:
      'Enable management of Service Accounts in the UI. Controls the visibility of the Service Accounts admin page and the Convert User to Service Account action in User Admin.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  CustomerRequireWithdrawalApproval: {
    key: 'customer.RequireWithdrawalApproval',
    description: '',
    valueType: 'boolean',
    flagType: 'feature',
  },

  CustomerRequireDepositApproval: {
    key: 'customer.RequireDepositApproval',
    description: '',
    valueType: 'boolean',
    flagType: 'feature',
  },

  CustomerTransactionAutoProposalMode: {
    key: 'customer.TransactionAutoProposalMode',
    description:
      'Mode for auto-proposing transaction details from treasury-account-links. When the mode is set to None, the system should not propose or initiate transfers. Default is None.',
    valueType: 'string',
    flagType: 'configuration',
  },

  CustomerExecHandlingV2: {
    key: 'customer.ExecHandlingV2',
    description:
      'If true, enables the execution decision logs feature on the customer order timeline. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  CustomerMaxCustomerCreditLimitCount: {
    key: 'customer.MaxCustomerCreditLimitCount',
    description: 'Maximum number of customer credit limits allowed for the organization.',
    valueType: 'number',
    flagType: 'configuration',
  },

  EnableAccountSegregation: {
    key: 'trading.EnableAccountSegregation',
    description: 'Enable full Account Segregation functionality',
    valueType: 'boolean',
    flagType: 'configuration',
  },

  EnableAccountSegregationConfiguration: {
    key: 'ui.EnableAccountSegregationConfiguration',
    description: 'Enable subset of Account Segregation functionality specifically for user/account configuration.',
    valueType: 'boolean',
    flagType: 'configuration',
  },

  EnableTransferSubAccountFilter: {
    key: 'ui.EnableTransferSubAccountFilter',
    description:
      'Enable Sub Account filter on the Transfers form. Only takes effect when trading.EnableAccountSegregation or ui.EnableAccountSegregationConfiguration is also enabled.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableAccountSegregationShowAllMarketAccounts: {
    key: 'ui.EnableAccountSegregationShowAllMarketAccounts',
    description:
      'when Account Segregation is enabled, bypass the Read permission check on the MarketAccountProvider (safety measure for initial rollout in 2.50.3)',
    valueType: 'boolean',
    flagType: 'configuration',
  },

  BalancePublishMarketAccountNames: {
    key: 'portfolio.BalancePublishMarketAccountNames',
    description:
      'An array of string market account names defining the accounts the user can publish balance updates to',
    valueType: 'json',
    flagType: 'configuration',
  },

  BalancePublishMarketNames: {
    key: 'portfolio.BalancePublishMarketNames',
    description:
      'An array of string market names defining the markets the user can publish balance updates to. Defaults to ["internal"].',
    valueType: 'json',
    flagType: 'configuration',
  },

  FlexibleTransferAmountAssets: {
    key: 'portfolio.LPSettle.FlexibleTransferAmountAssets',
    description:
      'Comma-separated list of assets that allow transfer amount overrides within one MinIncrement of the instruction amount.',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  EnableOptionTrading: {
    key: 'oms.OptionTrading.Enabled',
    description: 'If true, shows Calls/Puts Multilegs in the Multileg screen dropdown. Backend flag.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableOptionsChainView: {
    key: 'ui.EnableOptionsChainView',
    description:
      "If true, shows 'Option' in tablist and flex layout selection. Existing Option tabs will be removed if toggled off.",
    valueType: 'boolean',
    flagType: 'feature',
  },

  OMSPriceProtectionDisabled: {
    key: 'oms.PriceProtection.Disabled',
    description: '',
    valueType: 'boolean',
    flagType: 'feature',
  },

  TradingDisableAll: {
    key: 'trading.DisableAll',
    description: '',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableMarketCredentials: {
    key: 'trading.EnableMarketCredentials',
    description: 'Allows editing of market credentials',
    valueType: 'boolean',
    flagType: 'configuration',
  },

  ShowSubAccountPositionPnLTagColumns: {
    key: 'ui.ShowSubAccountPositionPnLTagColumns',
    description: 'Enable PnLTag related columns for Sub Account Position blotters',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowIborAborReportEndOfDayMarkColumns: {
    key: 'ui.ShowIborAborReportEndOfDayMarkColumns',
    description: 'If true, shows the End of Day Mark columns in the ABOR/IBOR report blotter',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableSettleGroupValuesColumns: {
    key: 'ui.EnableSettleGroupValuesColumns',
    description:
      'If true, will show the Sub account position blotter columns related to the Position.SettleGroupValues properties',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableTradeBestExecution: {
    key: 'ui.EnableTradeBestExecution',
    description: 'Enable the trade best execution feature',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableTradesUserBackendFiltering: {
    key: 'ui.enableTradesUserBackendFiltering',
    description:
      'Enables backend filtering for Users on the Trades blotter. Defaults to false until 2.59 is rolled out.',
    valueType: 'boolean',
    flagType: 'migration',
    targetEnd: 'v2.59',
  },

  SettlementReportMaxTradeRecordsPaginated: {
    key: 'ui.SettlementReportMaxTradeRecordsPaginated',
    description:
      'For the Settlement Report exporting, defines how many records we allow the frontend to paginate when fetching the trades for a settlement over REST',
    valueType: 'number',
    flagType: 'configuration',
  },

  DisableEquityMarginRatioLimitCheck: {
    key: 'oms.DisableEquityMarginRatioLimitCheck',
    description:
      "Disable validation for Equity to Margin Ratio check on orders. Default is 0, meaning it's enabled by default.",
    valueType: 'boolean',
    flagType: 'configuration',
  },

  EnableAutoHedging: {
    key: 'ui.EnableAutoHedging',
    description: 'Enable viewing auto hedging controls and status updates.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableDynamicDeltaHedging: {
    key: 'ui.EnableDynamicDeltaHedging',
    description: 'Enable Dynamic Delta Hedging',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableOrderLinkInHedgeRule: {
    key: 'ui.EnableOrderLinkInHedgeRule',
    description: 'Enable stitching orders with hedge position status based on the HedgeRuleID',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableHedgeRuleCrossAssetRiskGroup: {
    key: 'ui.EnableHedgeRuleCrossAssetRiskGroup',
    description:
      'If true, enables Cross-Asset Risk Group on Position Autohedging rules (form, blotters, and hedge commands). Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  PositionHedgerHomeCurrency: {
    key: 'ui.PositionHedgerHomeCurrency',
    description: 'The home currency of the autohedger. The asset cannot be selected to be this',
    valueType: 'string',
    flagType: 'configuration',
  },

  EnableSupportModal: {
    key: 'ui.EnableSupportModal',
    description: 'If true, will allow to file a JIRA ticket directly from the UI',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableSupportModalPagerDuty: {
    key: 'ui.EnableSupportModalPagerDuty',
    description: 'If true, allows marking an issue as high priority and sending a PagerDuty alert to the CS team',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnablePortfolioSubAccountRecon: {
    key: 'ui.EnablePortfolioSubAccountRecon',
    description: 'Enables PortfolioSubAccountRecon for PMS-Lite usage (PMS full users get this by default)',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ReconResolveBreakExcludedSubAccounts: {
    key: 'ui.ReconResolveBreakExcludedSubAccounts',
    description: "An array of sub account names to exclude from the Resolve Break drawer's allocation selector",
    valueType: 'json',
    flagType: 'configuration',
  },

  DisableCustomerMarketAccounts: {
    key: 'ui.DisableCustomerMarketAccounts',
    description: 'Disable loading of all customer market accounts. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  FilterMarketAccountsByPermissionActionAll: {
    key: 'ui.FilterMarketAccountsByPermissionActionAll',
    description: "Filter market accounts by permission action 'All'. Defaults to false.",
    valueType: 'boolean',
    flagType: 'feature',
  },

  ForceSessionReplay: {
    key: 'ui.ForceSessionReplay',
    description: 'Force Datadog session replay regardless of sample rate',
    valueType: 'boolean',
    flagType: 'feature',
  },

  RequireCustomerBalancesCounterpartyFilter: {
    key: 'ui.RequireCustomerBalancesCounterpartyFilter',
    description: 'Ensures counterparty is a required field on the Customer Balances screen (needed for retail scale)',
    valueType: 'boolean',
    flagType: 'feature',
  },

  RequireCustomerPricingRulesCounterpartyFilter: {
    key: 'ui.RequireCustomerPricingRulesCounterpartyFilter',
    description:
      'Ensures counterparty is a required field on the Customer Pricing Rules screen (needed for retail scale). Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  LoadGlobalCustomerPricingRules: {
    key: 'ui.LoadGlobalCustomerPricingRules',
    description:
      'When false, customer pricing rules are loaded when visiting a pricing rules screen instead of at app startup. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ViewDealerMarginPages: {
    key: 'ui.ViewDealerMarginPages',
    description: 'If true, will allow viewing the Margin Management pages in dealer',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ViewCounterpartyMonitoring: {
    key: 'ui.ViewCounterpartyMonitoring',
    description:
      'If true, enables Counterparty Monitoring under Operations. Requires VIEW_DEALER permission and ViewDealerMarginPages. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowDealerCustomerMarginAssetTransactionsTab: {
    key: 'ui.ShowDealerCustomerMarginAssetTransactionsTab',
    description:
      'If true, shows legacy Customer Asset Transactions under Dealer > Admin > Customer Margin. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  AllowSyntheticSymbolsOnManualCustomerTrade: {
    key: 'ui.AllowSytheticSymbolsOnManualCustomerTrade',
    description:
      'By default, non-synthetic symbols are filtered out from the Customer Book and Modify Trade Dialog. Set this flag to true if all available options should be visible.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  AllowModifyCustomerUserEmail: {
    key: 'ui.AllowModifyCustomerUserEmail',
    description:
      'By default, the customer user email is not editable after creation. [DEAL-4939] Set this flag to true to allow editing the email of the existing customer user. Defaults to true for Talos users.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  CheckCustomerBalanceOnNewCustomerTrade: {
    key: 'ui.CheckCustomerBalanceOnNewCustomerTrade',
    description:
      'Checks that counterparty/marketaccount has balance to create trade, and will render confirm flow if not.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  RestrictCustomerTradeByMarginEnabled: {
    key: 'ui.RestrictCustomerTradeByMarginEnabled',
    description:
      'When true, restrict manual customer trade booking by market-account margin status: margin-enabled accounts accept only non-spot securities; non-margin accounts accept only spot. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  SupportNotionalMultiplierInCustomerTrade: {
    key: 'ui.SupportNotionalMultiplierInCustomerTrade',
    description:
      'Use security PositionCurrency for customer manual trade quantity display and API Currency when NotionalMultiplier != 1.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  CredentialAgeAlertDays: {
    key: 'ui.CredentialAgeAlertDays',
    description: 'Number of days before a credential expires to show an alert',
    valueType: 'number',
    flagType: 'configuration',
  },

  CredentialAgeWarningDays: {
    key: 'ui.CredentialAgeWarningDays',
    description: 'Number of days before a credential expires to show a warning',
    valueType: 'number',
    flagType: 'configuration',
  },

  CredentialAgeBannerMuteDays: {
    key: 'ui.muteCredentialsAgeWarningDays',
    description: 'Number of days to mute credential expires banner',
    valueType: 'number',
    flagType: 'configuration',
  },

  CredentialAgeWarningIgnoreList: {
    key: 'ui.CredentialAgeWarningIgnoreList',
    description: 'List of markets to ignore when showing the credentials age warning banner',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  EnableSecurityMasterRequiredFiltering: {
    key: 'ui.EnableSecurityMasterRequiredFiltering',
    description: 'Enable Security Master required filtering',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableThemeTypeSelection: {
    key: 'ui.EnableThemeTypeSelection',
    description: 'Lets the user select the dark/light or auto adjusted theme',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnablePopoutWindows: {
    key: 'ui.EnablePopoutWindows',
    description: 'Enable popout window feature',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableMobileLayout: {
    key: 'ui.EnableMobileLayout',
    description: 'Enable the mobile layout experience. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  RequireMobileUITraderRole: {
    key: 'ui.requireMobileUITraderRole',
    description: 'If true, mobile order and RFQ trading actions require the Mobile UI Trader role. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableAdditionalTradingPanels: {
    key: 'ui.EnableAdditionalTradingPanels',
    description: 'Enable additional trading panels like order book, trade history and trading view chart',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableLadderPanels: {
    key: 'ui.EnableLadderPanels',
    description: 'Enable volume and price ladders',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableTradingSpaces: {
    key: 'ui.EnableTradingSpaces',
    description: 'Enable trading spaces, allows the user to add spaces and switch between them. Defaults to true.',
    valueType: 'boolean',
    flagType: 'migration',
  },

  EnableMonitoringSpaces: {
    key: 'ui.EnableMonitoringSpaces',
    description: 'Enable monitoring spaces, allows the user to add spaces and switch between them',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableDealerSpaces: {
    key: 'ui.EnableDealerSpaces',
    description: 'Enable dealer spaces, allowing the user to add spaces and switch between them. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  NonSpotDelta1DefaultQty: {
    key: 'ui.NonSpotDelta1DefaultQty',
    description: 'Toggle between BaseCurrency and Contract quantity for non-spot delta1 multileg',
    valueType: 'string',
    flagType: 'configuration',
  },

  TradingLimitsThresholdCurrencies: {
    key: 'ui.TradingLimitsThresholdCurrencies',
    description:
      'Defines the list of Assets/Currencies available in Threshold Asset/Currency dropdowns in Trading Limits. Default: BTC, EUR, USD.',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  EnableCrossAssetRiskGroupPositionLimits: {
    key: 'ui.EnableCrossAssetRiskGroupPositionLimits',
    description: 'If true, enables cross-asset risk group targets for Sub Account Position Limits. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCrossAssetRiskGroupDeltaRiskCheck: {
    key: 'ui.EnableCrossAssetRiskGroupDeltaRiskCheck',
    description:
      'If true, empty Cross-Asset Risk Groups are interpreted as "All assets"; otherwise, they are interpreted as "No assets". Activate this in sync with backend runtime config subaccount.EnableDeltaRiskCheck. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  UseLegacyCounterpartyLimits: {
    key: 'ui.UseLegacyCounterpartyLimits',
    description: 'Toggling these flags to true will use the legacy component for each Entity Admin screen',
    valueType: 'boolean',
    flagType: 'feature',
  },

  UseLegacyCustomerAggregations: {
    key: 'ui.UseLegacyCustomerAggregations',
    description: '',
    valueType: 'boolean',
    flagType: 'feature',
  },

  UnifiedLiquiditySelfServeAssets: {
    key: 'ui.UnifiedLiquiditySelfServeAssets',
    description:
      'Restrict the list of assets that are available as Source and Unified Asset in Unified Liquidity Admin',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  EnableUnifiedLiquiditySelfServe: {
    key: 'ui.EnableUnifiedLiquiditySelfServe',
    description: '',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EntityAdminMaxBulkEditRows: {
    key: 'ui.EntityAdminMaxBulkEditRows',
    description:
      'TODO until we have better bulk edit support in the UI, prevent bulk editing of more than X rows. Default is 10,000.',
    valueType: 'number',
    flagType: 'feature',
  },

  EnableNotifications: {
    key: 'ui.EnableNotifications',
    description: 'Enable In-app Notifications Displays. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableNotificationsDestinationsAdmin: {
    key: 'ui.EnableNotificationsDestinationsAdmin',
    description: 'Enable Destinations Admin (Email/Slack/Telegram) under Admin > Notifications. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  DismissAllNotificationsBatchSize: {
    key: 'ui.DismissAllNotificationsBatchSize',
    description: 'Maximum number of notifications that can be archived in a single batch operation *',
    valueType: 'number',
    flagType: 'configuration',
  },

  EnableTradePredictions: {
    key: 'ui.EnableTradePredictions',
    description: 'Enables Trade Predictions component for VWAP orders *',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnablePreTrade: {
    key: 'ui.EnablePreTrade',
    description: 'Enables Pre-Trade analysis functionality on the order form. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableOrderModificationDiffs: {
    key: 'ui.EnableOrderModificationDiffs',
    description: 'Enables order modification diffs in the confirmation card. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  OrderFormMarketSelectionAsyncRefDataTimeoutMs: {
    key: 'ui.OrderFormMarketSelectionAsyncRefDataTimeoutMs',
    description:
      'The time in milliseconds for how long the order form can wait for ref data requests before proceeding and initializing the order markets without them.',
    valueType: 'number',
    flagType: 'configuration',
  },

  EnableTransactTimeInNewBalanceTransaction: {
    key: 'ui.EnableTransactTimeInNewBalanceTransaction',
    description: 'If true, shows the TransactTime field in the New Balance Transaction form. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableNewCustomerBalanceTransactionDrawer: {
    key: 'ui.EnableNewCustomerBalanceTransactionDrawer',
    description: 'If true, shows the New Balance Transaction form with the Initiate method. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowDeprecatedAdminTools: {
    key: 'ui.ShowDeprecatedAdminTools',
    description: 'If true, shows the deprecated admin tools in the sidebar. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableOrderTrajectoryCharts: {
    key: 'ui.EnableOrderTrajectoryCharts',
    description: 'If true, shows the order trajectory chart(s) on the order analytics page. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableOrderTrajectoryPredictions: {
    key: 'ui.EnableOrderTrajectoryPredictions',
    description:
      'If true, shows order trajectory predictions in order trajectory charts. Requires EnableOrderTrajectoryCharts = true. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableOTCSupport: {
    key: 'ui.EnableOTCSupport',
    description:
      'If true, enables OTC support features in the UI (OTC blotters, OTC deal product builder, etc.), including OTC V2 features (Options for OTC Derivatives, FloatingRateLoans for Loans). Default is false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableOTCCustomerCounterpartySupport: {
    key: 'ui.EnableOTCCustomerCounterpartySupport',
    description:
      'If true, allows Customer Counterparties to have OTC Market Accounts added and be used in ManualDealTradeView. Default is false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  DisableCrossCurrencyQty: {
    key: 'ui.DisableCrossCurrencyQty',
    description: 'If true, disable the option to trade in the cross currency for synthetic crosses.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableIWouldAnalytics: {
    key: 'ui.EnableIWouldAnalytics',
    description: 'If true, enabled I Would analytics component for supported orders. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableTreasuryLinkAccountFiltering: {
    key: 'ui.EnableTreasuryLinkAccountFiltering',
    description:
      'If true, filters out OTC Settlement and Exchange Rebalance accounts with treasury links from balances (old behavior we want to remove). Default is false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  DisableBlotterErrorBanner: {
    key: 'ui.DisableBlotterErrorBanner',
    description: 'Set to true to disable the error banner in the blotter from showing at all. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableNegotiatedRFQFlow: {
    key: 'ui.EnableNegotiatedRFQFlow',
    description: 'Shows Negotiated/Systematic pricing mode toggle in RFQ form',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableExecutingOtherUserNegotiatedRFQ: {
    key: 'ui.EnableExecutingOtherUserNegotiatedRFQ',
    description:
      'When true, users with RFQ execution permission can trade negotiated RFQs belonging to another user after confirming.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnablePublishCustomRate: {
    key: 'ui.EnablePublishCustomRate',
    description: 'Enables publishing user-entered market-data rates to internal market accounts. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  DisableNonSTFNegotiatedRFQ: {
    key: 'ui.DisableNonSTFNegotiatedRFQ',
    description: 'Disables the PricingMode toggle, forcing the user to only be able to do SpreadToFixing RFQs',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowStFMarketsInNonStFNegotiatedRFQ: {
    key: 'ui.ShowStFMarketsInNonStFNegotiatedRFQ',
    description:
      'When true, negotiated non-spread-to-fixing RFQs can select markets that also support spread-to-fixing.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  CareOrderImportParser: {
    key: 'ui.CareOrderImportParser',
    description:
      'Parser to use for the Care Order import. Valid fields are "aladdin" or "unqork". Defaults to "aladdin".',
    valueType: 'string',
    flagType: 'configuration',
  },

  EnableCareOrderExternalIDField: {
    key: 'ui.EnableCareOrderExternalIDField',
    description:
      'When set to true, will show the External ID field in the care order form and all other places this value shows up',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCareOrderExternalComments: {
    key: 'ui.EnableCareOrderExternalComments',
    description:
      'When set to true, will show the External Comments field in the care order form and all other places this value shows up',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableImportBasketDetails: {
    key: 'ui.EnableImportBasketDetails',
    description: 'When true, will show a button that allows to import basket details via csv upload',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableImportBasketDetailsUnqork: {
    key: 'ui.EnableImportBasketDetailsUnqork',
    description:
      'When set to true, will show the API Import button to allow the user to import basket details to Talos from the unqork api by clicking it',
    valueType: 'boolean',
    flagType: 'feature',
  },

  BasketImportUnderlyingSecurityMapping: {
    key: 'ui.BasketImportUnderlyingSecurityMapping',
    description:
      'Stringified JSON object mapping basket SecurityDescription values to UnderlyingSecuritySymbol values. Example: {"BITCOIN":"BTC-USD"}',
    valueType: 'json',
    flagType: 'configuration',
  },

  ShowMakerApplyFixingDialogReferencePrice: {
    key: 'ui.ShowMakerApplyFixingDialogReferencePrice',
    description:
      'If set to true, will display a reference price to the maker when in the apply fixing dialog. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ShowMakerApplyFixingDialogIndexMarks: {
    key: 'ui.ShowMakerApplyFixingDialogIndexMarks',
    description:
      'If set to true, will display index marks by fixing row in the apply fixing dialog. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableIndexMarkSubs: {
    key: 'ui.EnableIndexMarkSubs',
    description: 'If set to true, enables IndexMark subscriptions to be made. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableRFQFormFundSecurities: {
    key: 'ui.EnableRFQFormFundSecurities',
    description: 'If set to true, will show the Fund product group option in the RFQ Form.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  BasketDefinitions: {
    key: 'basket.Definitions',
    description: 'Backend key for baskets',
    valueType: 'string',
    flagType: 'configuration',
  },

  BasketQuantityDetails: {
    key: 'basket.QuantityDetails',
    description: 'Backend key for baskets',
    valueType: 'string',
    flagType: 'configuration',
  },

  UnqorkProductIDMap: {
    key: 'rfq.UnqorkProductIDMap',
    description: 'A mapping between Unqork "productId" properties and Talos BasketSymbols (funds)',
    valueType: 'json',
    flagType: 'configuration',
  },

  UnqorkAuthorizedParticipantMap: {
    key: 'rfq.UnqorkAuthorizedParticipantMap',
    description: 'A mapping between Unqork "authorizedParticipant" properties and Talos Counterparty.Name',
    valueType: 'json',
    flagType: 'configuration',
  },

  EnableCareOrderFormFundField: {
    key: 'ui.EnableCareOrderFormFundField',
    description: 'When true, will show the Fund field in the Care Order Form',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableInKindCareOrders: {
    key: 'ui.EnableInKindCareOrders',
    description: 'When true, will show the Settlement Type toggle in the Care Order Form.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  CareOrderInitiatingFromValues: {
    key: 'ui.CareOrderInitiatingFromValues',
    description: 'Comma-separated list of values for the Care Order Initiating From field dropdown',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  RepoDealPrimeBrokerValues: {
    key: 'ui.RepoDealPrimeBrokerValues',
    description: 'Comma-separated list of prime broker values for repo deal booking.',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  CareOrderExportMakerList: {
    key: 'ui.CareOrderExportMakerList',
    description:
      'String array of market names to append maker bid/offer columns for in Care Order CSV exports. Eg ["coinbase","kraken"]',
    valueType: 'json',
    flagType: 'configuration',
  },

  EnableCareOrderSettleDateField: {
    key: 'ui.EnableCareOrderSettleDateField',
    description: 'When true, will allow the taker to set a Settlement Date on Care Orders',
    valueType: 'boolean',
    flagType: 'feature',
  },

  SupportAdjustedSettlementDate: {
    key: 'ui.SupportAdjustedSettlementDate',
    description:
      'When true, enables support for getting adjusted settlement dates from the backend. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  DefaultBasketSettleOffsetOverride: {
    key: 'ui.DefaultBasketSettleOffsetOverride',
    description:
      'Example: {"GBTC":{"Sell":2}} - Override the default settle offset for a fund side in the Care Order form. This is a JSON stringified object.',
    valueType: 'json',
    flagType: 'configuration',
  },

  EnableTakerMarketplaceETFRFQFlow: {
    key: 'ui.EnableTakerMarketplaceETFRFQFlow',
    description: 'Big flag to enable the ETFRFQ flow in general for Takers, guards a bunch of things',
    valueType: 'boolean',
    flagType: 'feature',
  },

  TruncateCareOrderID: {
    key: 'ui.TruncateCareOrderID',
    description: 'When true, strips "-" from CareOrderID before sending it as PostTradeClearingID.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableMakerMarketplaceETFRFQFlow: {
    key: 'ui.EnableMakerMarketplaceETFRFQFlow',
    description: 'Big flag to enable the ETFRFQ flow in general for Makers, guards a bunch of things',
    valueType: 'boolean',
    flagType: 'feature',
  },

  AllowCancellingCareOrderChildOrder: {
    key: 'ui.AllowCancellingCareOrderChildOrder',
    description: 'When enabled, allows the user to (force) cancel orders associated with Care Orders.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  AllowCancellingS2FCustomerOrder: {
    key: 'ui.AllowCancellingS2FCustomerOrder',
    description: 'When enabled, allows the user to (force) cancel Customer Orders that are SpreadToFixing.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCareOrderWriting: {
    key: 'ui.EnableCareOrderWriting',
    description:
      'When false, will remove all options which write (create, update, delete) Care Orders in the UI. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCareOrderOrders: {
    key: 'ui.EnableCareOrderOrders',
    description: 'When true, lets the user trade care orders via Orders and not just RFQs.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  CareOrderBlockDirectExecution: {
    key: 'careorder.BlockDirectExecution',
    description:
      'When true, blocks users from trading Care Orders directly. Care Orders must be netted into a Working Order before they can be traded in order to go through compliance.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCareOrderManagement: {
    key: 'ui.EnableCareOrderManagement',
    description: 'When true, enables the care-order management blotter panel.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCareOrderNetting: {
    key: 'ui.EnableCareOrderNetting',
    description:
      'When true, enables care-order netting actions and WorkingOrder subscriptions in the care-order blotter.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  WorkingRequireOrderActivation: {
    key: 'working.RequireOrderActivation',
    description: 'When enabled, a working order requires approval by a second trader before it can be routed against',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCareOrderManualFills: {
    key: 'ui.EnableCareOrderManualFills',
    description:
      'When true, enables manually filling care orders from the care-order blotter context menu. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableCareOrderManualFillsConfirmationTextCopying: {
    key: 'ui.EnableCareOrderManualFillsConfirmationTextCopying',
    description:
      'When true, enables copying confirmation text from the manual fill dialog context menu. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableTakerModifyFixing: {
    key: 'ui.EnableTakerModifyFixing',
    description: 'When true, will allow Taker users (Trader Admins) to modify fixing details of terminal orders',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableMakerModifyFixing: {
    key: 'ui.EnableMakerModifyFixing',
    description: 'When true, will allow Maker users (Trader Admins) to modify fixing details of terminal orders',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableQuotePleaseImprove: {
    key: 'ui.EnableQuotePleaseImprove',
    description:
      'When set to true and when applicable, will show a Please Improve button to the user in the Quote panel',
    valueType: 'boolean',
    flagType: 'feature',
  },

  SpreadToFixingSymbolIndexesMap: {
    key: 'rfq.SpreadToFixingSymbolIndexesMap',
    description: 'Json blob mapping of Symbol to indexes',
    valueType: 'json',
    flagType: 'configuration',
  },

  FixingPriceDialogMarkets: {
    key: 'ui.FixingPriceDialogMarkets',
    description:
      'Comma-separated list of markets to use (with useMarketDataSnapshot) to prime the fixing price dialog with a default price',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  EnableManualPricingPanelAvailableAmountDetails: {
    key: 'ui.EnableManualPricingPanelAvailableAmountDetails',
    description:
      'If true, will show the Available Amount Details box at the bottom of the Manual Pricing Panel. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableFlatCareOrderBlotterExport: {
    key: 'ui.EnableFlatCareOrderBlotterExport',
    description:
      'Enable this flag to have the Care Order export be just a flat list of child orders. These rows will be enriched with certain fields from the CO.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableTakerValidateFixing: {
    key: 'ui.EnableTakerValidateFixing',
    description: 'When enabled, will let the taker user open the Validate Fixing dialog.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableTakerCopyQuoteFundTradeDetails: {
    key: 'ui.EnableTakerCopyQuoteFundTradeDetails',
    description:
      'Set to true to allow the taker to copy details from their fund-related RFQs (in units). Lets them copy strings such as "5u Create GBTC NAV+2.1" to their clipboard.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  FundCareOrderDefaultSubAccounts: {
    key: 'ui.FundCareOrderDefaultSubAccounts',
    description:
      'Stringified JSON key-value mapping of FundDefinition.Symbol to SubAccount.Name. When a Fund in this record is selected in the CO Form, the SubAccount it is mapped to will be defaulted to.',
    valueType: 'json',
    flagType: 'configuration',
  },

  EnableApplyFixingDialogV2: {
    key: 'ui.EnableApplyFixingDialogV2',
    description:
      'Set to true to use the new Apply Fixing dialog which supports multi-token fixings as well as single-token fixings',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableModifyFixingDialogV2: {
    key: 'ui.EnableModifyFixingDialogV2',
    description: 'Set to true to use the new Modify Fixing dialog (V2) for multi-token fixing support',
    valueType: 'boolean',
    flagType: 'feature',
  },

  UseFundDefinitionAndTradableIndexesStreams: {
    key: 'ui.UseFundDefinitionAndTradableIndexesStreams',
    description:
      'When set to true, will switch from using the old basket definitions and indexing org configs, to reading the equivalent data from ws: FundDefinition and TradableIndexes subs',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableRFQFormFundField: {
    key: 'ui.EnableRFQFormFundField',
    description:
      'When true, shows the Fund field in the RFQ Form (when applicable). Requires ui.UseFundDefinitionAndTradableIndexesStreams to also be true to appear',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableBottomUpPricingPreviews: {
    key: 'ui.EnableBottomUpPricingPreviews',
    description:
      'Set to true to have the UI request the /preview-price endpoint when the maker is pricing a multitoken-quote bottom-up.',
    valueType: 'boolean',
    flagType: 'migration',
    targetEnd: 'v2.55.1',
  },

  EnableRFQBlotterV2: {
    key: 'ui.EnableRFQBlotterV2',
    description:
      'A development flag guarding the usage of the new RFQ (Quotes) blotter for principal. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnrichRFQBlotterV2WithCareOrders: {
    key: 'ui.EnrichRFQBlotterV2WithCareOrders',
    description:
      'Set to true to have the RFQ v2 blotter open a ws sub to Care Orders, enriching the Quotes shown with Care Orders with applicable, and making available the relevant columns to display CO information',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableUserAPIKey: {
    key: 'ui.EnableUserAPIKey',
    description:
      'When disabled, the API Key menu will not be available in the principal UI settings menu. Defaults to true.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  ClientLogoName: {
    key: 'ui.ClientLogoName',
    description: 'Filename matching client identifier in assets/logos (example.svg)',
    valueType: 'string',
    flagType: 'configuration',
  },

  ClientTitle: {
    key: 'ui.ClientTitle',
    description: 'Custom document title for client branding',
    valueType: 'string',
    flagType: 'configuration',
  },

  RetailScale: {
    key: 'ui.RetailScale',
    description:
      'If true, enables optimisations suited to very large datasets that occur in retail-scale deployments (e.g. 600k+ customers), including preserving source ordering instead of eagerly sorting large selector lists. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  BlockSubscriptions: {
    key: 'ui.BlockSubscriptions',
    description:
      'Comma-separated list of subscription names to block at the WebSocket level. Use this to temporarily disable problematic subscriptions that are causing issues for a client in case of an emergency. NOTE: IN A MULTI-STREAM SUBSCRIPTION, ALL STREAMS IN THE SUBSCRIPTION WILL BE BLOCKED IF ANY ONE OF THEM IS IN THE LIST. Example: "BalanceDelta,BalanceSnapshot"',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  EnableHaircutRatio: {
    key: 'ui.EnableHaircutRatio',
    description: 'If true, enables the HaircutRatio parameter for multileg securities and orders. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnableNetworkTwo: {
    key: 'ui.EnableNetworkTwo',
    description: 'If true, displays a "Network 2" indicator in the Talos Admin header. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  CoinMetricsApiKey: {
    key: 'ui.CoinMetricsApiKey',
    description: 'API key for the CoinMetrics Pro tier. Leave empty for community-only access.',
    valueType: 'string',
    flagType: 'configuration',
  },

  UnifiedQuoteCurrencyGroups: {
    key: 'ui.CustomerSecurityUnifiedQuoteCurrencyGroups',
    description:
      'Pipe-delimited list of quote currency groups, where each group is a comma-delimited list of currencies treated as interchangeable when validating UnderlyingCrossSymbol selections. Example: "USD,USDT|EUR,EURC" defines two groups: {USD, USDT} and {EUR, EURC}. If absent or empty, falls back to strict quote currency matching (existing behavior).',
    valueType: 'commaSeparatedList',
    flagType: 'configuration',
  },

  EnablePredictionMarkets: {
    key: 'ui.EnablePredictionMarkets',
    description: 'Enable Prediction Markets features in the UI.',
    valueType: 'boolean',
    flagType: 'feature',
  },

  EnablePredictionMarketPriceGrid: {
    key: 'ui.EnablePredictionMarketPriceGrid',
    description: 'Enable the Prediction Market Price Grid. Defaults to false.',
    valueType: 'boolean',
    flagType: 'feature',
  },
} as const satisfies Record<string, OrgConfigKeySpec>;

/** Union of all registered configuration key strings. */
export type OrgConfigurationKeyValue = (typeof OrgConfigurationKeys)[keyof typeof OrgConfigurationKeys]['key'];
